import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useEffect, useCallback, useMemo } from 'react';
import { isNotDefined, bound, _cs } from '@togglecorp/fujs';
import ListView from '../ListView/index.js';
import useSizeTracking from '../../hooks/useSizeTracking.js';
import { genericMemo } from '../../utils/index.js';
import styles from './styles.css.js';

const emptyList = [];

function VirtualizedListView(props) {
  const {
    className,
    itemHeight = 16,
    elementRef: elementRefFromProps,
    data: dataFromProps,
    buffer = 1,
    pending,
    keySelector,
    reverse,
    componentRef,
    ...otherProps
  } = props;
  const data = dataFromProps ?? emptyList;
  const [scrollOffset, setScrollOffset] = React.useState(undefined);
  const internalRef = React.useRef(null);
  const elementRef = elementRefFromProps ?? internalRef;
  const idleCallbackRef = React.useRef();
  const size = useSizeTracking(elementRef);
  const height = size === null || size === void 0 ? void 0 : size.height;
  useEffect(() => {
    if (componentRef) {
      componentRef.current = {
        scrollTo: key => {
          if (!elementRef.current) {
            return;
          }

          const itemIndex = data.findIndex((item, index) => keySelector(item, index) === key);

          if (itemIndex === -1) {
            console.error('Cannot scroll to key', key);
            return;
          }

          elementRef.current.scrollTop = itemHeight * itemIndex;
        }
      };
    }
  }, [data, keySelector, elementRef, componentRef, itemHeight]);
  const handleScroll = useCallback(e => {
    if (e.target !== elementRef.current) {
      return;
    }

    if (idleCallbackRef.current) {
      window.cancelIdleCallback(idleCallbackRef.current);
    }

    idleCallbackRef.current = window.requestIdleCallback(() => {
      if (elementRef.current) {
        setScrollOffset(elementRef.current.scrollTop);
      }
    }, {
      timeout: 200
    });
  }, [elementRef]); // Initially set scroll offset

  useEffect(() => {
    if (elementRef.current) {
      setScrollOffset(elementRef.current.scrollTop);
    }
  }, [elementRef]);
  const [renderData, topDummyHeight, bottomDummyHeight, offset] = useMemo(() => {
    if (data.length <= 2 * buffer) {
      // NOTE: we do not need to set offset because this is a case
      // without virtualization
      return [data, 0, 0, 0];
    }

    if (isNotDefined(height)) {
      return [[], 0, 0, 0];
    }

    const containerHeight = height;
    const startIndex = bound(Math.floor((scrollOffset ?? 0) / itemHeight) - buffer, 0, data.length - 1);
    const endIndex = bound(startIndex + Math.ceil(containerHeight / itemHeight) + 2 * buffer, 0, data.length - 1);
    const topHeight = startIndex * itemHeight;
    const bottomHeight = (data.length - 1 - endIndex) * itemHeight;
    const actualStartIndex = reverse ? data.length - 1 - endIndex : startIndex;
    const actualEndIndex = reverse ? data.length - 1 - startIndex : endIndex;
    return [data.slice(actualStartIndex, actualEndIndex + 1), topHeight, bottomHeight, actualStartIndex];
  }, [data, itemHeight, scrollOffset, buffer, height, reverse]);
  const totalHeight = itemHeight * data.length;
  const listViewHeight = totalHeight - topDummyHeight - bottomDummyHeight;
  const wrapperContainerStyle = useMemo(() => ({
    height: `${totalHeight}px`
  }), [totalHeight]);
  const listViewStyle = useMemo(() => ({
    height: `${listViewHeight}px`,
    transform: `translateY(${topDummyHeight}px)`
  }), [listViewHeight, topDummyHeight]);
  return /*#__PURE__*/React.createElement("div", {
    ref: elementRef,
    className: _cs(styles.virtualizedListView, className),
    onScroll: handleScroll
  }, /*#__PURE__*/React.createElement("div", {
    style: wrapperContainerStyle
  }, /*#__PURE__*/React.createElement(ListView, _extends({}, otherProps, {
    grouped: false,
    reverse: reverse,
    indexOffset: offset,
    data: renderData,
    pending: pending || data.length > 0 && renderData.length <= 0,
    direction: "vertical",
    keySelector: keySelector,
    style: listViewStyle
  }))));
}

var index = genericMemo(VirtualizedListView);

export { index as default };
//# sourceMappingURL=index.js.map
